/**
 * FP3-074 — Super Admin Organization Console.
 *
 *   /platform/org/:orgId
 *
 * Platform-owner-only "who is this org and who do I call?" page.
 * Clicking an org name in the Platform Dashboard table lands the
 * super admin here. The page surfaces:
 *
 *   1. Organization identity / status header (name, type, access,
 *      created, last activity, classification badge).
 *   2. Main Contacts card — PROMINENT. Primary + secondary contacts
 *      plus internal notes. Inline editable; saves via the existing
 *      PATCH /api/organizations/:id (super_admin only).
 *   3. Overview / counts card — schools, admins, providers,
 *      assistants, students, pending invites, pending access
 *      requests. Each count links into the matching `?as_org=` drill
 *      where one exists.
 *   4. Action panel — vertical buttons that reuse the existing
 *      drill-in routes + the same Add School / Invite Org Admin
 *      modals exposed on the Platform Dashboard. No hard-delete.
 *   5. Supporting details — schools list, org admins list.
 *
 * Does NOT mint Stripe customers, manage subscriptions, or run any
 * migrations. The contact fields are optional, blank-by-default,
 * and stored on the organization document without a schema bump.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Buildings,
  CalendarBlank,
  ChartPieSlice,
  CreditCard,
  DownloadSimple,
  EnvelopeSimple,
  GraduationCap,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  ShieldCheck,
  Trash,
  UserPlus,
  Users,
  Warning,
  Wrench,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ClassificationBadge from "@/components/platform/ClassificationBadge";
import AddSchoolModal from "@/components/platform/AddSchoolModal";
import InviteUserModal from "@/components/admin/InviteUserModal";
// FP3-105 — Super Admin permanent organization deletion (Danger Zone).
import PlatformDeleteModal from "@/components/platform/PlatformDeleteModal";

const STATUS_TONES = {
  active: { bg: "#E8F0EB", fg: "#1E3F33" },
  pilot: { bg: "#E7EDF3", fg: "#1F3F4F" },
  trial: { bg: "#E7EDF3", fg: "#1F3F4F" },
  trialing: { bg: "#E7EDF3", fg: "#1F3F4F" },
  limited: { bg: "#F5EFE3", fg: "#7A6A2C" },
  paused: { bg: "#F8EFEA", fg: "#7A3A2C" },
  canceled: { bg: "#F8EFEA", fg: "#7A3A2C" },
  past_due: { bg: "#F8EFEA", fg: "#7A3A2C" },
  inactive: { bg: "#F1F2F4", fg: "#3F4756" },
  membership: { bg: "#E8F0EB", fg: "#1E3F33" },
  not_required: { bg: "#F1F2F4", fg: "#3F4756" },
};

function Chip({ label, testid }) {
  if (!label) return <span className="text-[11.5px] text-[#9AA0A6]">—</span>;
  const t = STATUS_TONES[label] || { bg: "#F1F2F4", fg: "#3F4756" };
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.fg }}
    >
      {label}
    </span>
  );
}

function CountRow({ label, value, onClick, testid }) {
  const body = (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[12.5px] text-[#3F4756]">{label}</span>
      <span className="text-[14px] font-semibold text-[#1F2937] tabular-nums">
        {Number.isFinite(value) ? value : "—"}
      </span>
    </div>
  );
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className="w-full text-left rounded-md px-2 -mx-2 hover:bg-[#FBFAF6] transition-colors"
      >
        {body}
      </button>
    );
  }
  return (
    <div data-testid={testid} className="px-2 -mx-2">
      {body}
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, testid, tone = "default", disabled = false }) {
  const palette = {
    default:
      "text-[#1F2937] border-[#E5E3DB] hover:bg-[#F3F1EC] hover:border-[#3F4756]",
    primary:
      "text-white bg-[#1E3F33] border-[#1E3F33] hover:bg-[#163129]",
    platform:
      "text-[#0F3554] border-[#CCE0EC] bg-[#F4F8FB] hover:bg-[#E6F1F8] hover:border-[#3B7AA5]",
  }[tone];
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-3.5 py-2.5 rounded-lg text-[13px] font-medium border transition-colors ${palette} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <Icon size={15} weight="duotone" />
      <span className="truncate">{label}</span>
      <ArrowRight size={12} className="ml-auto opacity-50" />
    </button>
  );
}

function ContactBlock({ label, name, role, email, phone, testidPrefix, derived }) {
  const empty = !name && !role && !email && !phone;
  // FP3-079 — When manual contact fields are blank but a derived
  // contact candidate exists (solo owner / fallback admin / access
  // request), render the derived contact in a clearly-labeled
  // "Derived from …" block so super admin can immediately email
  // the workspace owner without first having to type the same info
  // into the manual primary contact fields.
  if (empty && derived && (derived.email || derived.name)) {
    const subject = encodeURIComponent("Frame Practice");
    return (
      <div
        data-testid={`${testidPrefix}-block`}
        className="space-y-0.5"
      >
        <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
          {label}
        </p>
        <p
          data-testid={`${testidPrefix}-derived-name`}
          className="text-[14px] font-semibold text-[#1F2937] heading-font"
        >
          {derived.name || "—"}
          {derived.role ? (
            <span className="ml-2 text-[12px] font-normal text-[#6B7280]">
              · {derived.role}
              {derived.specialty ? ` · ${derived.specialty}` : ""}
            </span>
          ) : null}
        </p>
          {derived.email && (
            <p
              data-testid={`${testidPrefix}-derived-email`}
              className="text-[12.5px] flex items-center gap-2 flex-wrap"
            >
              <a
                href={`mailto:${derived.email}`}
                className="text-[#1E3F33] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33]"
              >
                {derived.email}
              </a>
              <a
                href={`mailto:${derived.email}?subject=${subject}`}
                data-testid={`${testidPrefix}-derived-email-btn`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[#1E3F33]/30 text-[11px] font-medium text-[#1E3F33] hover:bg-[#E8F0EB] transition-colors"
              >
                <EnvelopeSimple size={11} weight="duotone" /> Email
              </a>
            </p>
          )}
        <p
          data-testid={`${testidPrefix}-derived-hint`}
          className="text-[10.5px] text-[#9AA0A6] italic mt-0.5"
        >
          {derived.source_hint || "Derived contact · not manually set"}
        </p>
      </div>
    );
  }
  return (
    <div data-testid={`${testidPrefix}-block`} className="space-y-0.5">
      <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
        {label}
      </p>
      {empty ? (
        <p
          data-testid={`${testidPrefix}-empty`}
          className="text-[12.5px] text-[#9AA0A6] italic"
        >
          Not set
        </p>
      ) : (
        <>
          <p
            data-testid={`${testidPrefix}-name`}
            className="text-[14px] font-semibold text-[#1F2937] heading-font"
          >
            {name || "—"}
            {role ? (
              <span className="ml-2 text-[12px] font-normal text-[#6B7280]">
                · {role}
              </span>
            ) : null}
          </p>
          {email && (
            <p data-testid={`${testidPrefix}-email`} className="text-[12.5px] flex items-center gap-2 flex-wrap">
              <a
                href={`mailto:${email}`}
                className="text-[#1E3F33] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33]"
              >
                {email}
              </a>
              <a
                href={`mailto:${email}?subject=${encodeURIComponent("Frame Practice")}`}
                data-testid={`${testidPrefix}-email-btn`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[#1E3F33]/30 text-[11px] font-medium text-[#1E3F33] hover:bg-[#E8F0EB] transition-colors"
              >
                <EnvelopeSimple size={11} weight="duotone" /> Email
              </a>
            </p>
          )}
          {phone && (
            <p
              data-testid={`${testidPrefix}-phone`}
              className="text-[12.5px] text-[#3F4756]"
            >
              {phone}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function OrganizationConsole() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingContacts, setEditingContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addSchoolOpen, setAddSchoolOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Editable contact field state. Initialised from the loaded org doc
  // whenever editing begins so cancelling discards unsaved edits.
  const [form, setForm] = useState({
    primary_contact_name: "",
    primary_contact_role: "",
    primary_contact_email: "",
    primary_contact_phone: "",
    secondary_contact_name: "",
    secondary_contact_role: "",
    secondary_contact_email: "",
    secondary_contact_phone: "",
    internal_notes: "",
  });

  const refresh = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    api
      .getPlatformOrgDetail(orgId)
      .then((r) => {
        const payload = r.data || {};
        setData(payload);
        const org = payload.organization || {};
        setForm({
          primary_contact_name: org.primary_contact_name || "",
          primary_contact_role: org.primary_contact_role || "",
          primary_contact_email: org.primary_contact_email || "",
          primary_contact_phone: org.primary_contact_phone || "",
          secondary_contact_name: org.secondary_contact_name || "",
          secondary_contact_role: org.secondary_contact_role || "",
          secondary_contact_email: org.secondary_contact_email || "",
          secondary_contact_phone: org.secondary_contact_phone || "",
          internal_notes: org.internal_notes || "",
        });
      })
      .catch(() => {
        toast.error("Could not load this organization");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    // Inline fetch (rather than calling `refresh()`) so the
    // react-hooks/set-state-in-effect rule sees the setState calls
    // directly tied to a stable dependency (orgId), not a callback
    // closure that recomposes every render.
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    api
      .getPlatformOrgDetail(orgId)
      .then((r) => {
        if (cancelled) return;
        const payload = r.data || {};
        setData(payload);
        const org = payload.organization || {};
        setForm({
          primary_contact_name: org.primary_contact_name || "",
          primary_contact_role: org.primary_contact_role || "",
          primary_contact_email: org.primary_contact_email || "",
          primary_contact_phone: org.primary_contact_phone || "",
          secondary_contact_name: org.secondary_contact_name || "",
          secondary_contact_role: org.secondary_contact_role || "",
          secondary_contact_email: org.secondary_contact_email || "",
          secondary_contact_phone: org.secondary_contact_phone || "",
          internal_notes: org.internal_notes || "",
        });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Could not load this organization");
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const isSuperAdmin = user?.permission_role === "super_admin";

  const drillTo = (path) =>
    navigate(`${path}?as_org=${encodeURIComponent(orgId)}`);

  const org = data?.organization || null;
  const counts = data?.counts || {};
  const schools = data?.schools || [];
  const orgAdmins = data?.org_admins || [];
  const lastActivity = data?.last_activity_at || "";

  const billingPostureLabel = useMemo(() => {
    if (!org) return "—";
    if (org.stripe_subscription_id_present) return "Subscribed";
    if (org.stripe_customer_id_present) return "Customer (no subscription)";
    return "Not connected";
  }, [org]);

  // FP3-074 — Defensive client gate. Backend already returns 403 to
  // non-super-admin, but redirecting keeps the URL honest. We use
  // <Navigate> rather than a useEffect+navigate to avoid a state
  // update during effect, and place it AFTER all hook calls so the
  // rules-of-hooks order stays stable across renders.
  if (user && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await api.updateOrganization(orgId, { ...form });
      toast.success("Main Contacts saved");
      setEditingContacts(false);
      refresh();
    } catch {
      toast.error("Could not save contacts");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    const o = org || {};
    setForm({
      primary_contact_name: o.primary_contact_name || "",
      primary_contact_role: o.primary_contact_role || "",
      primary_contact_email: o.primary_contact_email || "",
      primary_contact_phone: o.primary_contact_phone || "",
      secondary_contact_name: o.secondary_contact_name || "",
      secondary_contact_role: o.secondary_contact_role || "",
      secondary_contact_email: o.secondary_contact_email || "",
      secondary_contact_phone: o.secondary_contact_phone || "",
      internal_notes: o.internal_notes || "",
    });
    setEditingContacts(false);
  };

  if (loading) {
    return (
      <div
        data-testid="org-console-loading"
        className="px-6 py-10 text-[13px] text-[#6B7280]"
      >
        Loading organization…
      </div>
    );
  }

  if (!org) {
    return (
      <div
        data-testid="org-console-not-found"
        className="px-6 py-10 text-[13px] text-[#6B7280]"
      >
        Organization not found.{" "}
        <Link
          to="/platform/dashboard"
          className="text-[#1F4F73] underline decoration-1 underline-offset-[3px]"
        >
          Back to Platform Dashboard
        </Link>
      </div>
    );
  }

  // FP3-079 — Derive primary + secondary contact candidates from the
  // backend's `derived_contacts` block when the manual contact fields
  // are blank. Priority: solo_owner (for individual_provider orgs) →
  // earliest org_admin → latest access request. Secondary slot uses
  // the next-best non-duplicate candidate so super admin can see at
  // least one workable email even on a fresh pilot org. Derived
  // contacts are READ-ONLY display fallbacks; they never overwrite
  // the manual primary_contact_* fields.
  const derivedAll = data?.derived_contacts || {};
  const buildCandidate = (raw, hint) => {
    if (!raw || !(raw.email || raw.name)) return null;
    return { ...raw, source_hint: hint };
  };
  const candidates = [
    buildCandidate(
      derivedAll.solo_owner,
      "Derived from solo provider account"
    ),
    buildCandidate(
      derivedAll.fallback_admin,
      "Derived from first org admin"
    ),
    buildCandidate(
      derivedAll.latest_access_request,
      "Derived from access request"
    ),
  ].filter(Boolean);
  // Dedup by email so we don't show the same person twice if (e.g.)
  // the solo owner ALSO appears as the only org_admin.
  const seen = new Set();
  const unique = [];
  for (const c of candidates) {
    const k = (c.email || "").toLowerCase();
    if (k && seen.has(k)) continue;
    if (k) seen.add(k);
    unique.push(c);
  }
  const primaryDerived = unique[0] || null;
  const secondaryDerived = unique[1] || null;

  return (
    <div className="px-6 py-6 max-w-[1280px] mx-auto" data-testid="org-console">
      {/* Back link */}
      <Link
        to="/platform/dashboard"
        data-testid="org-console-back"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1F4F73] hover:text-[#0F3554] mb-4"
      >
        <ArrowLeft size={13} /> Back to Platform Dashboard
      </Link>

      {/* ── 1. Identity / status header ─────────────────────────── */}
      <header
        data-testid="org-console-header"
        className="mb-6 pb-5 border-b border-[#E5E3DB]"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1
                data-testid="org-console-name"
                className="text-[28px] font-semibold text-[#1F2937] heading-font leading-tight"
              >
                {org.name || "Untitled organization"}
              </h1>
              <ClassificationBadge
                row={org}
                testid={`org-console-classification-${org.id}`}
              />
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Chip
                label={org.account_type || ""}
                testid="org-console-account-type"
              />
              <Chip label={org.access_type || ""} testid="org-console-access" />
              <Chip
                label={org.membership_status || ""}
                testid="org-console-membership"
              />
              <span className="text-[11.5px] text-[#6B7280]">
                Created{" "}
                <span data-testid="org-console-created" className="text-[#3F4756]">
                  {org.created_at ? org.created_at.slice(0, 10) : "—"}
                </span>
                <span className="mx-1.5 text-[#D2D6DC]">·</span>
                Last activity{" "}
                <span data-testid="org-console-last-activity" className="text-[#3F4756]">
                  {lastActivity ? lastActivity.slice(0, 10) : "—"}
                </span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* ── 2. Main Contacts (prominent) ────────────────────────── */}
      <Card
        data-testid="org-console-contacts-card"
        className="mb-6 border border-[#E5E3DB] shadow-sm"
      >
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font">
                Main Contacts
              </h2>
              <p className="text-[11.5px] text-[#6B7280] mt-0.5">
                Primary, secondary, and internal notes for this organization.
                Only super admin can see or edit this.
              </p>
            </div>
            {!editingContacts && (
              <Button
                type="button"
                data-testid="org-console-contacts-edit-btn"
                onClick={() => setEditingContacts(true)}
                variant="outline"
                className="gap-1.5 border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB] hover:text-[#1E3F33]"
              >
                <PencilSimple size={13} weight="duotone" /> Edit contacts
              </Button>
            )}
          </div>

          {editingContacts ? (
            <div className="space-y-5">
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-2">
                  Primary contact
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <FieldInput
                    label="Name"
                    testid="primary-contact-name-input"
                    value={form.primary_contact_name}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, primary_contact_name: v }))
                    }
                  />
                  <FieldInput
                    label="Role / title"
                    testid="primary-contact-role-input"
                    value={form.primary_contact_role}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, primary_contact_role: v }))
                    }
                  />
                  <FieldInput
                    label="Email"
                    type="email"
                    testid="primary-contact-email-input"
                    value={form.primary_contact_email}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, primary_contact_email: v }))
                    }
                  />
                  <FieldInput
                    label="Phone"
                    testid="primary-contact-phone-input"
                    value={form.primary_contact_phone}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, primary_contact_phone: v }))
                    }
                  />
                </div>
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-2">
                  Secondary contact
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <FieldInput
                    label="Name"
                    testid="secondary-contact-name-input"
                    value={form.secondary_contact_name}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, secondary_contact_name: v }))
                    }
                  />
                  <FieldInput
                    label="Role / title"
                    testid="secondary-contact-role-input"
                    value={form.secondary_contact_role}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, secondary_contact_role: v }))
                    }
                  />
                  <FieldInput
                    label="Email"
                    type="email"
                    testid="secondary-contact-email-input"
                    value={form.secondary_contact_email}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, secondary_contact_email: v }))
                    }
                  />
                  <FieldInput
                    label="Phone"
                    testid="secondary-contact-phone-input"
                    value={form.secondary_contact_phone}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, secondary_contact_phone: v }))
                    }
                  />
                </div>
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-2">
                  Internal notes
                </p>
                <Textarea
                  data-testid="org-console-internal-notes-input"
                  value={form.internal_notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, internal_notes: e.target.value }))
                  }
                  rows={3}
                  placeholder="Private notes only super admin can see."
                  className="text-[13px]"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F1F2F4]">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="org-console-contacts-cancel-btn"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  data-testid="org-console-contacts-save-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#1E3F33] hover:bg-[#163129] text-white"
                >
                  {saving ? "Saving…" : "Save contacts"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              <ContactBlock
                label="Primary contact"
                testidPrefix="primary-contact"
                name={org.primary_contact_name}
                role={org.primary_contact_role}
                email={org.primary_contact_email}
                phone={org.primary_contact_phone}
                derived={primaryDerived}
              />
              <ContactBlock
                label="Secondary contact"
                testidPrefix="secondary-contact"
                name={org.secondary_contact_name}
                role={org.secondary_contact_role}
                email={org.secondary_contact_email}
                phone={org.secondary_contact_phone}
                derived={secondaryDerived}
              />
              <div className="md:col-span-2">
                <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-1">
                  Internal notes
                </p>
                <p
                  data-testid="org-console-internal-notes"
                  className={`text-[12.5px] whitespace-pre-line ${
                    org.internal_notes ? "text-[#3F4756]" : "text-[#9AA0A6] italic"
                  }`}
                >
                  {org.internal_notes || "No internal notes recorded."}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 3 + 4. Overview/Counts + Action Panel side-by-side ─── */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Counts (2 cols) */}
        <Card
          data-testid="org-console-counts-card"
          className="lg:col-span-2 border border-[#E5E3DB] shadow-sm"
        >
          <CardContent className="p-5">
            <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
              Overview
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 divide-y sm:divide-y-0 divide-[#F1F2F4]">
              <CountRow
                label="Schools / campuses"
                value={counts.schools}
                testid="org-console-count-schools"
                onClick={() => drillTo("/admin/schools")}
              />
              <CountRow
                label="Org admins"
                value={counts.admins}
                testid="org-console-count-admins"
                onClick={() => drillTo("/admin/access")}
              />
              <CountRow
                label="Providers (primary)"
                value={counts.providers}
                testid="org-console-count-providers"
                onClick={() => drillTo("/admin/providers")}
              />
              <CountRow
                label="Assistants"
                value={counts.assistants}
                testid="org-console-count-assistants"
                onClick={() => drillTo("/admin/providers")}
              />
              <CountRow
                label="Students"
                value={counts.students}
                testid="org-console-count-students"
                onClick={() => drillTo("/admin/students")}
              />
              <CountRow
                label="Pending invites"
                value={counts.pending_invites}
                testid="org-console-count-invites"
                onClick={() => drillTo("/admin/access")}
              />
              <CountRow
                label="Pending access requests"
                value={counts.pending_access_requests}
                testid="org-console-count-access-requests"
                onClick={() => drillTo("/admin/access")}
              />
              <CountRow
                label="Billing posture"
                value={null}
                testid="org-console-billing-posture"
              />
            </div>
            <div className="mt-3 pt-3 border-t border-[#F1F2F4] flex items-center gap-2 text-[11.5px] text-[#6B7280]">
              <CreditCard size={13} weight="duotone" />
              <span data-testid="org-console-billing-posture-label">
                Stripe: {billingPostureLabel}
              </span>
              <span className="text-[#D2D6DC]">·</span>
              <span data-testid="org-console-billing-interval">
                Interval: {org.billing_interval || "—"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Action panel (1 col) */}
        <Card
          data-testid="org-console-actions-card"
          className="border border-[#E5E3DB] shadow-sm"
        >
          <CardContent className="p-5">
            <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
              Actions
            </h2>
            <div className="space-y-2">
              <ActionButton
                icon={ShieldCheck}
                label="Open as Org Admin view"
                tone="primary"
                testid="org-console-action-open"
                onClick={() => drillTo("/admin/dashboard")}
              />
              <ActionButton
                icon={UserPlus}
                label="Manage admins"
                tone="platform"
                testid="org-console-action-manage-admins"
                onClick={() => drillTo("/admin/access")}
              />
              <ActionButton
                icon={EnvelopeSimple}
                label="Quick-invite org admin"
                testid="org-console-action-quick-invite"
                onClick={() => setInviteOpen(true)}
              />
              <ActionButton
                icon={Plus}
                label="Add school / campus"
                testid="org-console-action-add-school"
                onClick={() => setAddSchoolOpen(true)}
              />
              <ActionButton
                icon={Buildings}
                label="Manage schools & access"
                testid="org-console-action-manage-schools"
                onClick={() => drillTo("/admin/schools")}
              />
              <ActionButton
                icon={Users}
                label="View providers"
                testid="org-console-action-providers"
                onClick={() => drillTo("/admin/providers")}
              />
              <ActionButton
                icon={GraduationCap}
                label="View students"
                testid="org-console-action-students"
                onClick={() => drillTo("/admin/students")}
              />
              <ActionButton
                icon={ChartPieSlice}
                label="Service fidelity"
                testid="org-console-action-fidelity"
                onClick={() => drillTo("/admin/service-fidelity")}
              />
              <ActionButton
                icon={CalendarBlank}
                label="Shared calendar"
                testid="org-console-action-calendar"
                onClick={() => drillTo("/admin/calendar")}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── 5. Supporting details: schools + org admins ─────────── */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card
          data-testid="org-console-schools-card"
          className="border border-[#E5E3DB] shadow-sm"
        >
          <CardContent className="p-5">
            <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
              Schools &amp; campuses ({schools.length})
            </h2>
            {schools.length === 0 ? (
              <p
                data-testid="org-console-schools-empty"
                className="text-[12.5px] text-[#9AA0A6] italic"
              >
                No schools yet. Use &ldquo;Add school / campus&rdquo; in the Actions panel
                to create the first one.
              </p>
            ) : (
              <ul className="divide-y divide-[#F1F2F4]">
                {schools.map((s) => (
                  <li
                    key={s.id}
                    data-testid={`org-console-school-${s.id}`}
                    className="py-2 flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-[#1F2937] truncate">
                        {s.name || "Untitled school"}
                      </p>
                      <p className="text-[11px] text-[#6B7280] truncate">
                        {s.email_domain || "—"}
                      </p>
                    </div>
                    <Chip
                      label={s.access_status || s.status || ""}
                      testid={`org-console-school-status-${s.id}`}
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card
          data-testid="org-console-admins-card"
          className="border border-[#E5E3DB] shadow-sm"
        >
          <CardContent className="p-5">
            <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font mb-3">
              Org admins ({orgAdmins.length})
            </h2>
            {orgAdmins.length === 0 ? (
              <p
                data-testid="org-console-admins-empty"
                className="text-[12.5px] text-[#9AA0A6] italic"
              >
                No org admins yet. Use &ldquo;Quick-invite org admin&rdquo; in the Actions
                panel to send the first invitation.
              </p>
            ) : (
              <ul className="divide-y divide-[#F1F2F4]">
                {orgAdmins.map((a) => (
                  <li
                    key={a.id}
                    data-testid={`org-console-admin-${a.id}`}
                    className="py-2"
                  >
                    <p className="text-[13px] font-medium text-[#1F2937]">
                      {[a.first_name, a.last_name].filter(Boolean).join(" ") ||
                        a.email}
                    </p>
                    <p className="text-[11px] text-[#6B7280] truncate">
                      <a
                        href={`mailto:${a.email}`}
                        className="text-[#1E3F33] underline decoration-1 underline-offset-[3px] decoration-[#8FA998]/60 hover:decoration-[#1E3F33]"
                      >
                        {a.email}
                      </a>
                      <span className="mx-1.5 text-[#D2D6DC]">·</span>
                      {a.permission_role}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── FP3-112B — Super-Admin FP3-110A Repair Dry-Run ──
          Temporary Super-Admin-only diagnostic. Read-only. Calls the
          already-deployed /admin/repair/fp3110a-assistant-rows/dry-run
          endpoint auto-scoped to THIS org. Displays every stale
          team_assignments row that would be deactivated by an Apply
          run, with student + assistant + supervising-provider names
          resolved server-side. NO Apply button — that stays gated
          behind the direct API call for now. */}
      <FP3110ARepairCard orgId={orgId} />

      {/* ── 6. Danger Zone — FP3-105 permanent organization deletion ── */}
      <Card
        data-testid="org-danger-zone"
        className="mt-6 border border-[#E3C4BE] bg-[#FDF9F8] shadow-sm"
      >
        <CardContent className="p-5">
          <h2 className="text-[15px] font-semibold text-[#7A1E1E] heading-font flex items-center gap-2">
            <Trash size={15} weight="duotone" /> Danger Zone
          </h2>
          <p className="text-[12px] text-[#6B7280] mt-1 max-w-2xl">
            Permanently deletes this organization and everything inside it —
            schools, students, providers, admins, records, and stored files.
            Every account in the organization immediately loses access. This
            cannot be undone.
          </p>
          <Button
            data-testid="org-delete-btn"
            onClick={() => setDeleteOpen(true)}
            className="mt-3 bg-[#7A1E1E] hover:bg-[#611414] text-white text-[12.5px]"
          >
            Permanently delete organization…
          </Button>
        </CardContent>
      </Card>

      <PlatformDeleteModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        targetType="organization"
        target={org}
        onDeleted={() => {
          setDeleteOpen(false);
          toast.success(`Organization "${org?.name}" permanently deleted.`);
          navigate("/platform/dashboard");
        }}
      />

      {/* Modals — reuse the same ones the Platform Dashboard uses. */}
      <AddSchoolModal
        open={addSchoolOpen}
        prefillOrganizationId={orgId}
        organizations={org ? [{ id: org.id, name: org.name }] : []}
        onClose={() => setAddSchoolOpen(false)}
        onCreated={() => {
          setAddSchoolOpen(false);
          refresh();
        }}
      />
      <InviteUserModal
        open={inviteOpen}
        prefillOrganizationId={orgId}
        prefillIntendedRole="org_admin"
        onClose={() => setInviteOpen(false)}
        onCreated={() => {
          setInviteOpen(false);
          refresh();
        }}
      />
    </div>
  );
}

function FieldInput({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block text-[12px] text-[#3F4756]">
      <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
        {label}
      </span>
      <Input
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[13px]"
      />
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────
// FP3-112B — Super-Admin FP3-110A Assistant-Row Repair Dry-Run card.
// FP3-112C — Adds the gated Apply flow (dry-run → confirmation modal
//            → server-side re-scan → 409 on count drift → mutation
//            → success payload → auto-rerun dry-run).
//
// Read-only diagnostic UI + a deliberate, high-friction repair action
// for the platform owner. Wraps the already-deployed backend endpoints
//   POST /admin/repair/fp3110a-assistant-rows/dry-run
//   POST /admin/repair/fp3110a-assistant-rows/apply
// and auto-scopes both to the organization the console is viewing.
//
//   • Apply appears ONLY after a fresh dry-run returned ≥1 stale row.
//   • Clicking Apply opens a confirmation modal that displays the
//     exact students that will be affected, plus two locked-in
//     inputs the operator must type verbatim:
//       - the phrase FP3110A_ASSISTANT_ROW_REPAIR
//       - the expected stale-row count (must equal the dry-run total)
//   • On Confirm the FE calls the backend Apply, which re-scans the
//     database and returns 409 if the row set drifted since the last
//     dry-run (concurrent edit safety net).
//   • After a successful Apply the FE:
//       1) captures + displays the change receipt (rows_changed,
//          students_touched, applied_at, actor_user_id, pre-image);
//       2) automatically re-runs the dry-run;
//       3) if stale_row_count == 0, marks the card "repaired" and
//          hides the Apply button.
//   • No auth tokens are displayed or logged. The existing
//     super_admin session is used verbatim by axios.
//   • Response payloads (dry-run + apply) remain in memory only;
//     the "Download JSON" button exports either the latest dry-run
//     OR the last apply receipt (whichever ran last).
// ────────────────────────────────────────────────────────────────────
const APPLY_CONFIRM_PHRASE = "FP3110A_ASSISTANT_ROW_REPAIR";

function FP3110ARepairCard({ orgId }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [applyReceipt, setApplyReceipt] = useState(null);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const runDryRun = async () => {
    if (!orgId || running) return;
    setRunning(true);
    setError("");
    try {
      const r = await api.fp3110aRepairDryRun(orgId);
      setResult(r.data);
      const n = (r.data && r.data.stale_row_count) || 0;
      if (n === 0) {
        toast.success("Dry-run complete — no stale rows detected.");
      } else {
        toast.success(`Dry-run complete — ${n} stale row${n === 1 ? "" : "s"} detected.`);
      }
    } catch (e) {
      const msg =
        (e && e.response && e.response.data && (e.response.data.detail || e.response.data.message)) ||
        (e && e.message) ||
        "Dry-run failed.";
      setError(String(msg));
      toast.error("Dry-run failed. See the card for details.");
    } finally {
      setRunning(false);
    }
  };

  const applyRepair = async () => {
    if (!orgId || applying || !result) return;
    const expected = result.stale_row_count || 0;
    if (expected <= 0) {
      toast.error("Nothing to apply — the last dry-run showed 0 stale rows.");
      return;
    }
    setApplying(true);
    setError("");
    try {
      // FP3-112C — Belt-and-braces safety: refresh the dry-run inside
      // the FE just before Apply. The backend will re-scan and 409
      // on drift, but this refresh gives the operator a chance to see
      // any drift BEFORE the mutation attempt (better UX than only
      // failing at Apply time).
      const fresh = await api.fp3110aRepairDryRun(orgId);
      setResult(fresh.data);
      const freshCount = (fresh.data && fresh.data.stale_row_count) || 0;
      if (freshCount !== expected) {
        setApplyModalOpen(false);
        toast.error(
          `Row count changed since last dry-run (was ${expected}, now ${freshCount}). Apply cancelled — review the refreshed list and retry.`,
          { duration: 8000 }
        );
        return;
      }
      const r = await api.fp3110aRepairApply(orgId, expected, APPLY_CONFIRM_PHRASE);
      setApplyReceipt(r.data);
      setApplyModalOpen(false);
      toast.success(
        `Apply complete — ${r.data.rows_changed || 0} row${(r.data.rows_changed || 0) === 1 ? "" : "s"} deactivated across ${r.data.students_touched || 0} student${(r.data.students_touched || 0) === 1 ? "" : "s"}.`,
        { duration: 8000 }
      );
      // Auto-rerun the dry-run so the operator sees the post-repair state.
      const post = await api.fp3110aRepairDryRun(orgId);
      setResult(post.data);
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      let msg = "Apply failed.";
      if (detail && typeof detail === "object") {
        // 409 mismatch shape: { code, message, expected_stale_rows, actual_stale_rows }
        if (detail.code === "expected_count_mismatch") {
          msg = `Row count changed on the server since the last dry-run (expected ${detail.expected_stale_rows}, now ${detail.actual_stale_rows}). Apply was blocked. Re-run the dry-run and retry.`;
        } else if (detail.message) {
          msg = detail.message;
        }
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (e && e.message) {
        msg = e.message;
      }
      setError(String(msg));
      toast.error("Apply failed. See the card for details.", { duration: 8000 });
    } finally {
      setApplying(false);
    }
  };

  const downloadJson = () => {
    const payload = applyReceipt || result;
    if (!payload) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const kind = applyReceipt ? "apply" : "dry-run";
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fp3110a-${kind}-${orgId || "platform"}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const staleCount = (result && result.stale_row_count) || 0;
  const canOpenApply = !!result && staleCount > 0 && !applying;

  return (
    <Card
      data-testid="fp3110a-repair-card"
      className="mt-6 border border-[#E5E3DB] shadow-sm bg-white"
    >
      <CardContent className="p-5">
        <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font flex items-center gap-2">
          <Wrench size={15} weight="duotone" className="text-[#1E3F33]" />
          FP3-110A · Assistant Assignment Dry-Run
        </h2>
        <p
          data-testid="fp3110a-safety-label"
          className="text-[12px] text-[#6B7280] mt-1 max-w-2xl leading-relaxed"
        >
          Dry-run is read-only. Scans this organization&rsquo;s{" "}
          <span className="font-mono">team_assignments</span> rows for
          assistants marked <span className="font-mono">active</span> with no
          matching entry in <span className="font-mono">assigned_providers</span>{" "}
          or <span className="font-mono">student_provider_assignments</span>.
          Apply is a separate, deliberately gated action requiring the exact
          confirm phrase and matching expected-count.
        </p>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            data-testid="fp3110a-run-dry-run-btn"
            onClick={runDryRun}
            disabled={running || !orgId}
            className="h-9 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-[12.5px] font-medium inline-flex items-center gap-2 disabled:opacity-40"
          >
            <MagnifyingGlass size={14} weight="bold" />
            {running ? "Running…" : "Run FP3-110A Assistant Assignment Dry-Run"}
          </Button>
          {result && (
            <Button
              data-testid="fp3110a-download-json-btn"
              type="button"
              onClick={downloadJson}
              variant="outline"
              className="h-9 rounded-lg text-[12.5px] border-[#d4d2cc] text-[#1F2937] hover:bg-[#F4F2EC] inline-flex items-center gap-2"
            >
              <DownloadSimple size={14} weight="bold" />
              Download JSON
            </Button>
          )}
          {canOpenApply && (
            <Button
              data-testid="fp3110a-open-apply-btn"
              type="button"
              onClick={() => setApplyModalOpen(true)}
              className="h-9 bg-[#7A1E1E] hover:bg-[#601515] text-white rounded-lg text-[12.5px] font-medium inline-flex items-center gap-2"
            >
              <Warning size={14} weight="bold" />
              Apply repair ({staleCount} row{staleCount === 1 ? "" : "s"})
            </Button>
          )}
        </div>

        {error && (
          <div
            data-testid="fp3110a-error"
            className="mt-4 rounded-lg border border-[#E3C4BE] bg-[#FDF9F8] text-[12.5px] text-[#7A1E1E] px-3 py-2 whitespace-pre-wrap break-words"
          >
            {error}
          </div>
        )}

        {applyReceipt && (
          <div
            data-testid="fp3110a-apply-receipt"
            className="mt-4 rounded-lg border border-[#B3D3B7] bg-[#F1F8F3] px-3 py-3"
          >
            <div className="text-[12px] font-semibold text-[#1E3F33] uppercase tracking-[0.08em]">
              Apply complete
            </div>
            <div className="text-[12.5px] text-[#1F2937] mt-1 leading-relaxed">
              <span className="font-medium">
                {applyReceipt.rows_changed || 0}
              </span>{" "}
              team_assignments row{applyReceipt.rows_changed === 1 ? "" : "s"}{" "}
              flipped <span className="font-mono">active → inactive</span>{" "}
              across{" "}
              <span className="font-medium">
                {applyReceipt.students_touched || 0}
              </span>{" "}
              student{applyReceipt.students_touched === 1 ? "" : "s"}. Historical
              rows are preserved in{" "}
              <span className="font-mono">team_assignments</span> with{" "}
              <span className="font-mono">deactivated_at</span>,{" "}
              <span className="font-mono">deactivated_by_user_id</span>, and{" "}
              <span className="font-mono">deactivated_reason=fp3110a_repair</span>{" "}
              stamped; a{" "}
              <span className="font-mono">fp3110a_repair</span> entry was pushed
              to each student&rsquo;s <span className="font-mono">routing_history</span>.
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11.5px] text-[#4B5563]">
              <span>
                <span className="font-semibold">Applied at:</span>{" "}
                <span className="font-mono">{applyReceipt.applied_at || "—"}</span>
              </span>
              <span>
                <span className="font-semibold">Actor:</span>{" "}
                <span className="font-mono break-all">
                  {applyReceipt.actor_user_id || "—"}
                </span>
              </span>
              <span>
                <span className="font-semibold">Scope:</span>{" "}
                <span className="font-mono break-all">
                  {(applyReceipt.scope && applyReceipt.scope.organization_id) ||
                    "platform_wide"}
                </span>
              </span>
            </div>
            {Array.isArray(applyReceipt.stale_rows) &&
              applyReceipt.stale_rows.length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
                    Records changed
                  </div>
                  <ul className="mt-1 text-[12px] text-[#1F2937] list-disc pl-5 space-y-1">
                    {applyReceipt.stale_rows.map((r, i) => (
                      <li key={i} data-testid={`fp3110a-receipt-row-${i}`}>
                        <span className="font-medium">{r.student_name || "(name unavailable)"}</span>
                        {r.student_readable_id && (
                          <span className="text-[#6B7280]"> · {r.student_readable_id}</span>
                        )}{" "}
                        —{" "}
                        <span className="font-mono">
                          {r.assistant_name || r.assistant_provider_id}
                        </span>{" "}
                        row deactivated
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        )}

        {result && (
          <div className="mt-4" data-testid="fp3110a-result">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <ResultTile
                label="Affected students"
                value={result.affected_students ?? 0}
                testid="fp3110a-affected-students"
              />
              <ResultTile
                label="Stale rows"
                value={result.stale_row_count ?? 0}
                testid="fp3110a-stale-row-count"
              />
              <ResultTile
                label="Assistants"
                value={(result.affected_assistants || []).length}
                testid="fp3110a-assistants-count"
              />
              <ResultTile
                label="Supervising providers"
                value={(result.affected_supervising_providers || []).length}
                testid="fp3110a-supervisors-count"
              />
            </div>

            {(result.stale_rows || []).length === 0 ? (
              <p
                data-testid="fp3110a-empty"
                className="mt-4 text-[12.5px] text-[#4B5563] italic"
              >
                No stale assistant rows detected in this organization. Nothing
                would change if Apply were run.
              </p>
            ) : (
              <div
                data-testid="fp3110a-stale-rows"
                className="mt-4 space-y-3"
              >
                {result.stale_rows.map((row, idx) => (
                  <StaleRowCard
                    key={`${row.student_id}-${row.assistant_provider_id}-${idx}`}
                    row={row}
                    index={idx}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
      {applyModalOpen && (
        <FP3110AApplyModal
          rows={(result && result.stale_rows) || []}
          expected={staleCount}
          applying={applying}
          onCancel={() => (applying ? null : setApplyModalOpen(false))}
          onConfirm={applyRepair}
        />
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// FP3-112C — Apply confirmation modal. Two locked-in inputs:
//   • confirm phrase must equal FP3110A_ASSISTANT_ROW_REPAIR verbatim
//   • expected-count must equal the current dry-run stale_row_count
// Both must be typed correctly before the Confirm button enables.
// The student list is displayed prominently so the operator cannot
// accidentally apply against the wrong scope.
// ────────────────────────────────────────────────────────────────────
function FP3110AApplyModal({ rows, expected, applying, onCancel, onConfirm }) {
  const [phrase, setPhrase] = useState("");
  const [count, setCount] = useState("");
  const phraseOk = phrase === APPLY_CONFIRM_PHRASE;
  const countOk = String(expected) === count.trim();
  const canConfirm = phraseOk && countOk && !applying;
  return (
    <div
      data-testid="fp3110a-apply-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]">
        <div className="p-5 border-b border-[#E5E3DB]">
          <h3 className="text-[16px] font-semibold text-[#7A1E1E] heading-font flex items-center gap-2">
            <Warning size={16} weight="fill" />
            Apply FP3-110A repair
          </h3>
          <p className="text-[12.5px] text-[#4B5563] mt-1 leading-relaxed">
            This deactivates the <span className="font-medium">{expected}</span>{" "}
            stale assistant <span className="font-mono">team_assignments</span>{" "}
            row{expected === 1 ? "" : "s"} listed below (active → inactive) and
            pushes an audit entry to each student&rsquo;s{" "}
            <span className="font-mono">routing_history</span>. Historical rows
            are preserved. Commitments, goals, schedules, sessions, and progress
            data are untouched. Only the qualifying rows change.
          </p>
        </div>
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          <div>
            <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-1">
              Students affected ({rows.length})
            </div>
            <ul
              data-testid="fp3110a-apply-modal-student-list"
              className="rounded-lg border border-[#E5E3DB] divide-y divide-[#E5E3DB]"
            >
              {rows.map((r, i) => (
                <li
                  key={`${r.student_id}-${i}`}
                  data-testid={`fp3110a-apply-modal-student-${i}`}
                  className="px-3 py-2 text-[12.5px]"
                >
                  <div className="font-medium text-[#1F2937]">
                    {r.student_name || "(name unavailable)"}
                    {r.student_readable_id && (
                      <span className="text-[#6B7280] font-normal">
                        {" "}
                        · {r.student_readable_id}
                      </span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-[#4B5563]">
                    Assistant row to deactivate:{" "}
                    <span className="font-medium text-[#1F2937]">
                      {r.assistant_name || r.assistant_provider_id}
                    </span>
                    {r.supervising_provider_name && (
                      <>
                        {" "}
                        · supervised by{" "}
                        <span className="font-medium text-[#1F2937]">
                          {r.supervising_provider_name}
                        </span>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <label className="block text-[12px] text-[#3F4756]">
            <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
              Type the confirm phrase verbatim
            </span>
            <span className="block mb-1 text-[11px] font-mono text-[#7A1E1E]">
              {APPLY_CONFIRM_PHRASE}
            </span>
            <Input
              data-testid="fp3110a-apply-modal-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              className="text-[13px] font-mono"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="block text-[12px] text-[#3F4756]">
            <span className="block mb-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
              Expected stale-row count (must equal {expected})
            </span>
            <Input
              data-testid="fp3110a-apply-modal-count"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="text-[13px] font-mono w-32"
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
        </div>
        <div className="p-4 border-t border-[#E5E3DB] flex items-center justify-end gap-2">
          <Button
            data-testid="fp3110a-apply-modal-cancel"
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={applying}
            className="h-9 rounded-lg text-[12.5px]"
          >
            Cancel
          </Button>
          <Button
            data-testid="fp3110a-apply-modal-confirm"
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="h-9 bg-[#7A1E1E] hover:bg-[#601515] text-white rounded-lg text-[12.5px] font-medium inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Warning size={14} weight="bold" />
            {applying ? "Applying…" : "Confirm and apply"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResultTile({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-[#E5E3DB] bg-[#FAFAF7] px-3 py-2"
    >
      <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
        {label}
      </div>
      <div className="text-[18px] font-semibold text-[#1F2937] tabular-nums mt-0.5">
        {value}
      </div>
    </div>
  );
}

function StaleRowCard({ row, index }) {
  const studentDisplay = row.student_name || "(name unavailable)";
  const assistantDisplay = row.assistant_name || "(name unavailable)";
  const supervisorDisplay = row.supervising_provider_name || "(none / not set)";
  return (
    <div
      data-testid={`fp3110a-stale-row-${index}`}
      className="rounded-lg border border-[#E5E3DB] bg-white px-3 py-2.5"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <RowField
          label="Student"
          name={studentDisplay}
          id={row.student_id}
          extra={row.student_readable_id}
          testidPrefix={`fp3110a-row-${index}-student`}
        />
        <RowField
          label="Assistant"
          name={assistantDisplay}
          id={row.assistant_provider_id}
          extra={row.assistant_email}
          testidPrefix={`fp3110a-row-${index}-assistant`}
        />
        <RowField
          label="Supervising provider"
          name={supervisorDisplay}
          id={row.supervising_provider_id || "—"}
          extra={row.supervising_provider_email}
          testidPrefix={`fp3110a-row-${index}-supervisor`}
        />
      </div>
      <div className="mt-2">
        <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
          Qualifying predicates
        </div>
        <ul
          data-testid={`fp3110a-row-${index}-reasons`}
          className="mt-1 text-[12px] text-[#4B5563] leading-relaxed list-disc pl-5"
        >
          {(row.reasons || []).map((r, i) => (
            <li key={i} className="font-mono">
              {r}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RowField({ label, name, id, extra, testidPrefix }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
        {label}
      </div>
      <div
        data-testid={`${testidPrefix}-name`}
        className="text-[13px] font-medium text-[#1F2937] break-words"
      >
        {name}
      </div>
      <div
        data-testid={`${testidPrefix}-id`}
        className="text-[11px] text-[#6B7280] font-mono break-all"
      >
        {id}
      </div>
      {extra && (
        <div
          data-testid={`${testidPrefix}-extra`}
          className="text-[11px] text-[#6B7280] break-all"
        >
          {extra}
        </div>
      )}
    </div>
  );
}
